import React, { useMemo } from 'react';
import MathText from './MathText';
import { OPTION_KEYS } from '../../utils/Constants';

// Word tiles for a Build-a-Sentence question live in the present option fields (A–H).
export const tilesOf = (q) => OPTION_KEYS.map((k) => q[k]).filter((t) => t && String(t).trim());

// Deterministic Fisher–Yates shuffle of [0..n-1], seeded by the question id (mulberry32 PRNG).
// If we rendered tiles in option order they may be systematically ordered (e.g. the reverse of
// the answer), which makes the sentence guessable. Shuffling the DISPLAY order — while the stored
// answer stays a list of original option indices — scrambles the tiles without touching grading.
// Seeding by question id keeps the order stable across re-renders (tiles don't jump on keystroke).
export function shuffledTileOrder(n, seed) {
    const arr = Array.from({ length: n }, (_, i) => i);
    let s = (seed || 1) >>> 0;
    const rand = () => {
        s = (s + 0x6d2b79f5) | 0;
        let t = Math.imul(s ^ (s >>> 15), 1 | s);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

// Sentence builder: tiles are the present option fields, shown in a stable shuffled order. Click a
// tile to append; click a placed tile to remove. The stored order is a list of ORIGINAL option
// indices (into the present options), so grading against metadata.correct_order is unaffected by
// the display shuffle. `onOrder(question_id, orderArray)` receives the new order.
export default function SentenceBuilder({ question, answer, onOrder }) {
    const tiles = tilesOf(question);
    const display = useMemo(() => shuffledTileOrder(tiles.length, question.question_id), [question.question_id, tiles.length]);
    const order = (answer && answer.order) || [];
    const used = new Set(order);
    const toggle = (i) => {
        if (used.has(i)) onOrder(question.question_id, order.filter((x) => x !== i));
        else onOrder(question.question_id, [...order, i]);
    };
    return (
        <div>
            <div style={{ minHeight: '2.5rem', border: '1px dashed #9ca3af', borderRadius: '0.5rem', padding: '0.5rem', marginBottom: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem', background: '#f9fafb' }}>
                {order.length === 0 && <span style={{ color: '#9ca3af' }}>Tap the words below to build the sentence…</span>}
                {order.map((i) => (
                    <button key={i} type="button" onClick={() => toggle(i)} className="quiz-option selected" style={{ padding: '0.3rem 0.6rem' }}>
                        <MathText text={tiles[i]} />
                    </button>
                ))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {display.map((i) => (
                    <button key={i} type="button" disabled={used.has(i)} onClick={() => toggle(i)} className="quiz-option" style={{ padding: '0.3rem 0.6rem', opacity: used.has(i) ? 0.35 : 1 }}>
                        <MathText text={tiles[i]} />
                    </button>
                ))}
            </div>
        </div>
    );
}
